import {first,  takeUntil, filter, flatMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { Observable, Subject } from 'rxjs';
import * as _ from 'lodash';
import * as moment from 'moment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';

@Injectable({ providedIn: 'root' })
export class BriefingService {
    repeatModes = [
        { key: 'HOURLY', val: 'Hourly'},
        { key: 'DAILY', val: 'Daily'},
        { key: 'EVERY_WEEKDAY', val: 'Every weekday (Monday to Friday)'},
        { key: 'EVERY_MON_WED_FRI', val: 'Every Monday, Wednesday and Friday'},
        { key: 'EVERY_TUE_THUR', val: 'Every Tuesday and Thursday'},
        { key: 'WEEKLY', val: 'Weekly'},
        { key: 'BIWEEKLY', val: 'Bi-weekly'},
        { key: 'MONTHLY', val: 'Monthly'}
    ];
    byDay = {
        'SU': 'Sunday',
        'MO': 'Monday',
        'TU': 'Tuesday',
        'WE': 'Wednesday',
        'TH': 'Thursday',
        'FR': 'Friday',
        'SA': 'Saturday'
    };

    briefingHistoryChanged = new Subject<string>();

    path: string = '/ws/ua/briefings';

    constructor(private api: ApiService, 
                private fb: FormBuilder, 
                private dialog: MatDialog,
                private snackbar: MatSnackBar){}

    getBriefing = briefingId => this.api.get(`/ws/briefing/${briefingId}`); 

    getBriefings = (params) => this.api.get(this.path, this.api.paramify(params));

    getUnauthBriefings = (params):Observable<any> => this.api.get('/ws/ua/briefings', this.api.paramify(params));

    getCurrentUserBriefings = () => this.api.get('/ws/user/currentUser/briefings');

    getSlackChannels = (token) => this.api.get(`/ws/slack/channels/${token}`);

    getBriefingSubscribers(briefingId, params) {
        return this.api.get('/ws/briefing/' + briefingId + '/subscribers', this.api.paramify(params));
    }

    subscribeToBriefing(briefingId: string, userId: string, streamId?: string): Observable<any> {
        return this.api.post(`/ws/user/${userId}/briefings/${briefingId}`, { streamId });
    }

    unsubscribeFromBriefing(briefingId: string, userId: string): Observable<any> {
        return this.api.delete(`/ws/user/${userId}/briefings/${briefingId}`);
    };

    inviteToBriefing = (briefingId, invitation) => this.api.post(`/ws/briefing/${briefingId}/invitations?isClient=true`, invitation);

    scheduleDescription = schedule => {
        let desc;
        if (schedule && schedule.scheduleMode === 'MANUAL') {
            desc = 'Manual';
        } else if (schedule && schedule.scheduleMode === 'RECURRING') {
            const key = schedule.freq === 'WEEKLY' && schedule.interval === 2 ? 'BIWEEKLY' : schedule.freq;
            desc = _.find(this.repeatModes, { key }).val;
            if (schedule.freq === 'WEEKLY' && schedule.byday) {
                desc = desc + ' on ' + schedule.byday.map(byday => this.byDay[byday]).join(', ');
            }
        }
        return desc || '';
    }

    buildBriefingForm = (cleanupTime$: Observable<void>): FormGroup => {
        const form =  this.fb.group({
            briefingTitle: ['', Validators.required],
            description: ['', Validators.required],
            created: [],
            type: ['', Validators.required],
            subjectLine: [],
            templateId: [],
            templateHeaderImageUrl: [],
            published: [],
            commentary: this.fb.group({
                title: [''],
                body: [''],
            }),
            settings: this.fb.group({
                slackChannel: [],
                slackToken: []
            }),
            schedule: this.fb.group({
                start: [''],
                scheduleMode: [''],
                sendHour: [''], 
                sendMinute: [''],
                sendTime: [''],
                timeZone: [''],
                freq: [''], 
                byday: [],
            })
        });

        form.get('type').valueChanges.pipe(takeUntil(cleanupTime$))
        .subscribe(type => {
            const settings = form.controls.settings;
            ['slackChannel', 'slackToken'].forEach(key => {
                const field = settings.get(key);
                field.setValidators(type === 'SLACK' ? Validators.required: []);
                field.updateValueAndValidity();
            });
            form.controls.templateId.setValidators(['SLACK', 'TEAMS'].includes(type) ? []: Validators.required);
            form.controls.templateId.updateValueAndValidity();

            form.controls.description.setValidators(type === 'TEAMS' ? [] : Validators.required);
            form.controls.description.updateValueAndValidity();
        });

        return form;
    }

    createBriefing = (briefing) =>  this.api.post('/ws/briefing/', briefing);

    saveBriefing = (briefing) => this.api.put(`/ws/briefing/${briefing.id}`, briefing);

    sendNow = (briefingId) => this.api.post(`/ws/briefing/${briefingId}/sendNow`, {});

    deleteBriefing = (briefingId) => this.api.delete('/ws/briefing/' + briefingId);

    confirmedDeleteBriefing = (briefing): Observable<any> => {
        return this.dialog.open(ConfirmationDialogComponent, { 
            data: {
                title: `Delete ${briefing.briefingTitle}?`,
                content: `
                    Warning! This will <b>permanently</b> remove this briefing from the system.
                    <br>By clicking the Delete button below, you are confirming that you are removing this briefing. 
                `, 
                actionLabel: 'Delete'}
        }).afterClosed()
        .pipe(
            first(),
            filter(doIt => doIt),
            flatMap(() => this.deleteBriefing(briefing.id)),
            tap(() => this.snackbar.open(`${briefing.briefingTitle} has been deleted.`, 'OK', { duration: 3000}))
        );
    }

    getSlackTokenForNewBriefing() {
        return this.api.get(`/ws/briefing/token`);
    }

    getHistory(briefingId, params?){
        return this.api.get(`/ws/briefing/${briefingId}/history`, this.api.paramify(params));
    };

    fetchGroupSubscribers(briefingId, params){
        return this.api.get(`/ws/briefing/${briefingId}/groups`, this.api.paramify(params));
    }

    getUserBriefings(params) {
        return this.api.get('/ws/user/briefings', this.api.paramify(params));
    }
}
